import * as React from 'react';
import orderBy from 'lodash/orderBy';
import { LineChart } from 'components/LineChart';
import { ComparisonWidget } from 'components/Widgets';
import { ApiContext } from 'context/ApiContext';
import {
  ICurrencyFormat,
  IRange,
  IYearOnYearChartSerie,
  IYearOnYearResponse,
} from 'utils/types';
import {
  SectionWrapper,
  HeaderLabel,
  HeaderTop,
  YearOnYearContent,
  YearOnYearChart,
  TableWidgets,
  YearOnYearWidget,
} from './Analysis.styles';
import moment from 'moment';
import { CHART_COLOR_PALLETTE, EXPORT_OPTIONS } from 'utils/constants';
import { ApSimpleDropdown } from '@alixpartners/ui-components';

const getPeriodLabelFromRange = (range: IRange) => {
  return `${moment(range.startDate).format('MMM YYYY')} - ${moment(
    range.endDate
  ).format('MMM YYYY')}`;
};

const YearOnYearComparisonView = ({
  downloadSpendYoYData,
  format,
  onExportChart,
  showSuccessNotification,
  yearOnYear,
}: {
  downloadSpendYoYData: () => Promise<any>;
  format: ICurrencyFormat;
  onExportChart: (c: any, f: string) => void;
  showSuccessNotification: () => void;
  yearOnYear: IYearOnYearResponse;
}) => {
  if (!yearOnYear) {
    return null;
  }
  const chartRef = React.createRef<any>();
  const quarters = orderBy(yearOnYear.quarters, 'quarter');
  const categories = quarters.map((q) => q.quarter);
  const tooltipDate = quarters.map(
    (q) => `${q.quarter} ${q.previousRange.startYear}`
  );
  const serieCurrent: IYearOnYearChartSerie = {
    categories,
    data: quarters.map((q) => q.currentSpend),
    color: CHART_COLOR_PALLETTE[0],
    legendLabel: getPeriodLabelFromRange(yearOnYear.range),
    tooltipDate,
    quarters: quarters,
  };
  const serieCompare: IYearOnYearChartSerie = {
    categories,
    data: quarters.map((q) => q.previousSpend),
    color: CHART_COLOR_PALLETTE[1],
    legendLabel: getPeriodLabelFromRange(yearOnYear.comparisonRange),
    tooltipDate,
    quarters: quarters,
  };
  const series = [serieCurrent, serieCompare];
  return (
    <SectionWrapper>
      <HeaderTop>
        <HeaderLabel>Year on year comparison</HeaderLabel>
        <ApSimpleDropdown
          iconName="outline_save_alt"
          onChange={(s: any) => {
            if (s.value === 'png') {
              if (chartRef && chartRef.current && chartRef.current.chart) {
                onExportChart(
                  chartRef.current.chart,
                  `TXR_year-on-year-comparison-${moment().format()}`
                );
              }
            } else if (s.value === 'xlsx') {
              downloadSpendYoYData().then(showSuccessNotification);
            }
          }}
          options={EXPORT_OPTIONS}
          label="Export"
        />
      </HeaderTop>
      <YearOnYearContent>
        <YearOnYearChart>
          <LineChart
            format={format}
            series={series}
            categories={categories}
            chartRefObj={chartRef}
          />
        </YearOnYearChart>
        <TableWidgets>
          <YearOnYearWidget>
            <ComparisonWidget
              data={[
                {
                  title: 'Year on Year Comparison',
                  ...yearOnYear.yoYComparisonWidget,
                },
              ]}
              format={format}
              isYTD
            />
          </YearOnYearWidget>
          <YearOnYearWidget>
            <ComparisonWidget
              data={[
                {
                  title: 'Quarterly movement',
                  ...yearOnYear.quarterlyMovementWidget,
                },
              ]}
              format={format}
            />
          </YearOnYearWidget>
        </TableWidgets>
      </YearOnYearContent>
    </SectionWrapper>
  );
};

export default (props: any) => (
  <ApiContext.Consumer>
    {({ format }) => <YearOnYearComparisonView {...props} format={format} />}
  </ApiContext.Consumer>
);
